import { Routes, Route, Navigate } from 'react-router-dom'
import { Layout } from './components/Layout'
import { DashboardPage } from './pages/Dashboard'
import { MeetingCenterPage } from './pages/MeetingCenter'
import { WeeklyStatusPage } from './pages/WeeklyStatus'
import { EvidenceHistoryPage } from './pages/EvidenceHistory'
import { GiftOperationsPage } from './pages/GiftOperations'
import { BoxPage } from './pages/Box'
import { AdaptersPage } from './pages/Adapters'
import { AclPage } from './pages/Acl'
import { AccessManagementPage } from './pages/AccessManagement'
import { AdminPage } from './pages/Admin'
import { ProposalsPage } from './pages/Proposals'
import { ProposalDetailPage } from './pages/ProposalDetail'
import { KpiDashboardPage } from './pages/KpiDashboard'
import { MasteryMapPage } from './pages/MasteryMap'
import { MemoryPage } from './pages/Memory'
import { MonitoringPage } from './pages/Monitoring'
import { OnboardingPage } from './pages/Onboarding'
import { LoginPage } from './pages/Login'
import { WorkspacesPage } from './pages/Workspaces'
import { StatsPage } from './pages/Stats'
import { BenchmarkCurationPage } from './pages/BenchmarkCuration'
import { JLocomoCurationPage } from './pages/JLocomoCuration'
import { useAppAuth } from './lib/auth'
import { defaultHref, hasAnyNavScreen, isScreenEnabled, type ScreenId } from './lib/nav-config'
import { useScreenSet } from './lib/use-screen-set'

function LoadingSpinner() {
  return (
    <div className="flex h-screen items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded } = useAppAuth()

  if (!isLoaded) {
    return <LoadingSpinner />
  }

  if (!isSignedIn) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function AppRoutes() {
  // WU-04: runtime-config の ui.enabledScreens で route を filter。
  // WU-07: capability bootstrap（/admin/me/bootstrap）との積集合。
  // 取得完了まで spinner（全画面露出フラッシュ・誤 redirect 防止）。
  // 無効画面への直接 URL アクセスは catch-all で有効画面へ redirect する。
  const screenSet = useScreenSet()

  if (screenSet.status === 'loading') {
    return <LoadingSpinner />
  }

  const enabled = screenSet.screens
  const on = (screen: ScreenId) => isScreenEnabled(screen, enabled)

  // capability × deploy の積集合が空（nav に出せる画面ゼロ）のときは route を張らない。
  // catch-all の Navigate が defaultHref('/') と往復して無限 redirect になるのを防ぐ。
  if (!hasAnyNavScreen(enabled)) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-2 bg-background text-center">
        <p className="text-lg font-semibold">表示可能な画面がありません</p>
        <p className="text-sm text-muted-foreground">
          アクセス権限とデプロイ設定をご確認のうえ、管理者に連絡してください。
        </p>
      </div>
    )
  }

  return (
    <Layout screens={enabled}>
      <Routes>
        {on('dashboard') && <Route path="/" element={<DashboardPage />} />}
        {on('weekly-status') && (
          <Route path="/weekly-status" element={<WeeklyStatusPage />} />
        )}
        {on('meeting-center') && (
          <Route path="/meeting-center" element={<MeetingCenterPage />} />
        )}
        {on('evidence-history') && (
          <Route path="/evidence-history" element={<EvidenceHistoryPage />} />
        )}
        {on('gift-operations') && (
          <Route path="/gift-operations" element={<GiftOperationsPage />} />
        )}
        {on('box') && <Route path="/box" element={<BoxPage />} />}
        {on('adapters') && <Route path="/adapters" element={<AdaptersPage />} />}
        {on('acl') && <Route path="/acl" element={<AclPage />} />}
        {on('access-management') && (
          <Route path="/access-management" element={<AccessManagementPage />} />
        )}
        {on('proposals') && <Route path="/proposals" element={<ProposalsPage />} />}
        {on('proposals') && (
          <Route path="/proposals/:proposalId" element={<ProposalDetailPage />} />
        )}
        {on('kpi') && <Route path="/kpi" element={<KpiDashboardPage />} />}
        {on('mastery') && <Route path="/mastery" element={<MasteryMapPage />} />}
        {on('monitoring') && <Route path="/monitoring" element={<MonitoringPage />} />}
        {on('onboarding') && <Route path="/onboarding" element={<OnboardingPage />} />}
        {on('memory-explore') && (
          <Route path="/memory-explore" element={<MemoryPage />} />
        )}
        {on('memory-explore') && (
          <Route path="/memory" element={<Navigate to="/memory-explore" replace />} />
        )}
        {on('admin') && <Route path="/admin" element={<AdminPage />} />}
        {on('benchmark-curation') && (
          <Route path="/benchmark-curation" element={<BenchmarkCurationPage />} />
        )}
        {on('j-locomo-curation') && (
          <Route path="/j-locomo-curation" element={<JLocomoCurationPage />} />
        )}
        {on('workspaces') && <Route path="/workspaces" element={<WorkspacesPage />} />}
        {on('stats') && <Route path="/stats" element={<StatsPage />} />}
        <Route path="*" element={<Navigate to={defaultHref(enabled)} replace />} />
      </Routes>
    </Layout>
  )
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <AppRoutes />
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}
